import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, FormControl, Autocomplete, Chip, Button,
  ToggleButtonGroup, ToggleButton, IconButton, InputLabel, Select, MenuItem,
  Paper, LinearProgress
} from '@mui/material';
import {
  FormatBold, FormatItalic, FormatStrikethrough, Code,
  Visibility, Edit, Delete, AttachFile, ErrorOutline,
  Add, Remove, Phone, OpenInNew, Reply, TextFields, ImageOutlined, Videocam, InsertDriveFile
} from '@mui/icons-material';
import { API_BASE_URL } from 'envConfig';
import { WhatsAppBusinessConfig, WhatsAppBusinessButton } from '../../../modules/Interfaces/InstanUpdates/Template';

type Props = {
  currentTemplate: any;
  setCurrentTemplate: (fn: any) => void;
  dataSets: any[];
  templateNameError: string;
  setTemplateNameError: (e: string) => void;
  companyId: string;
  getDisplayCollectionName: (name?: string) => string;
  onSaveError?: (error: string) => void;
  isEditing?: boolean;
};

const WA_BIZ_GREEN = '#25d366';
const WA_BIZ_GREEN_DARK = '#128c7e';

const META_TEMPLATE_CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'UTILITY', label: 'Utility' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
];

const META_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'en_US', label: 'English (US)' },
  { code: 'en_GB', label: 'English (UK)' },
  { code: 'es', label: 'Spanish' },
  { code: 'pt_BR', label: 'Portuguese (BR)' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ar', label: 'Arabic' },
  { code: 'zh_CN', label: 'Chinese (CN)' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'ru', label: 'Russian' },
  { code: 'tr', label: 'Turkish' },
  { code: 'nl', label: 'Dutch' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ms', label: 'Malay' },
  { code: 'th', label: 'Thai' },
  { code: 'vi', label: 'Vietnamese' },
];

const HEADER_TYPES: { value: 'none' | 'text' | 'image' | 'video' | 'document'; label: string; icon: React.ReactNode }[] = [
  { value: 'none', label: 'None', icon: null },
  { value: 'text', label: 'Text', icon: <TextFields fontSize="small" /> },
  { value: 'image', label: 'Image', icon: <ImageOutlined fontSize="small" /> },
  { value: 'video', label: 'Video', icon: <Videocam fontSize="small" /> },
  { value: 'document', label: 'Document', icon: <InsertDriveFile fontSize="small" /> },
];

const BUTTON_TYPES: { value: 'URL' | 'PHONE_NUMBER' | 'QUICK_REPLY'; label: string; icon: React.ReactNode }[] = [
  { value: 'URL', label: 'Visit Website', icon: <OpenInNew fontSize="small" /> },
  { value: 'PHONE_NUMBER', label: 'Call Phone', icon: <Phone fontSize="small" /> },
  { value: 'QUICK_REPLY', label: 'Quick Reply', icon: <Reply fontSize="small" /> },
];

const WaBizSection: React.FC<{ title: string; number: number; children: React.ReactNode; optional?: boolean }> = ({ title, number, children, optional }) => (
  <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2, borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: WA_BIZ_GREEN, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{number}</Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      {optional && <Chip label="Optional" size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />}
    </Box>
    {children}
  </Paper>
);

const formatWhatsAppBiz = (text: string) =>
  (text || '')
    .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    .replace(/~([^~]+)~/g, '<del>$1</del>')
    .replace(/```([^`]+)```/g, '<code style="font-family:monospace;background:rgba(0,0,0,0.06);padding:2px 4px;border-radius:3px">$1</code>')
    .replace(/\n/g, '<br>');

export const WhatsAppBusinessTemplate: React.FC<Props> = ({
  currentTemplate,
  setCurrentTemplate,
  dataSets,
  templateNameError,
  setTemplateNameError,
  companyId,
  getDisplayCollectionName,
  onSaveError: _onSaveError,
  isEditing: _isEditing = false
}) => {
  const nameRegex = /^[a-z0-9_]*$/;

  const getConfig = (): WhatsAppBusinessConfig => currentTemplate?.whatsappBusinessConfig || {
    language: 'en_US', header: { type: 'none' }, footer: '', buttons: [],
  };

  const updateConfig = (partial: Partial<WhatsAppBusinessConfig>) => {
    setCurrentTemplate((prev: any) => prev ? { ...prev, whatsappBusinessConfig: { ...getConfig(), ...partial } } : null);
  };

  const [bodyValue, setBodyValue] = useState<string>(String(currentTemplate?.templateContent || ''));
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
  const bodyRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [headerMediaFile, setHeaderMediaFile] = useState<File | null>(null);
  const [headerMediaPreview, setHeaderMediaPreview] = useState<string>('');
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [lastFetchedDataSet, setLastFetchedDataSet] = useState('');

  useEffect(() => {
    setBodyValue(String(currentTemplate?.templateContent || ''));
  }, [currentTemplate?.templateContent]);

  useEffect(() => {
    return () => { if (headerMediaPreview) URL.revokeObjectURL(headerMediaPreview); };
  }, [headerMediaPreview]);

  const config = getConfig();

  const getDisplayTemplateName = (fullTemplateName?: string) => {
    if (!fullTemplateName) return '';
    const parts = fullTemplateName.split('-');
    if (parts.length >= 3 && parts[0] === 'IUT' && parts[1] === companyId) {
      return parts.slice(2).join('-');
    }
    return fullTemplateName;
  };

  const fetchDataSetColumns = async (dataSetId: string) => {
    if (!dataSetId) { setAvailableColumns([]); setLastFetchedDataSet(''); return; }
    if (lastFetchedDataSet === dataSetId) return;
    try {
      setLoadingColumns(true);
      const selected = dataSets.find((ds: any) => ds._id === dataSetId);
      if (!selected) return;
      const full = selected.collectionName;
      const parts = full.split('-');
      const base = parts.length >= 3 ? parts.slice(2).join('-') : full;
      const resp = await fetch(`${API_BASE_URL}/iu/ds/cmp/set/entry/schema/${encodeURIComponent(base)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('tokenid')}` }
      });
      const json = await resp.json();
      const schema = json.data || json;
      const cols = Object.keys(schema.paths || {}).filter((k: string) => !['_id', 'createdAt', 'updatedAt', '__v'].includes(k));
      setAvailableColumns(cols);
      setLastFetchedDataSet(dataSetId);
    } catch { setAvailableColumns([]); setLastFetchedDataSet(''); }
    finally { setLoadingColumns(false); }
  };

  useEffect(() => {
    if (currentTemplate?.collectionId) fetchDataSetColumns(currentTemplate.collectionId);
    else setAvailableColumns([]);
  }, [currentTemplate?.collectionId, dataSets]);

  const applyFormatting = (format: 'bold' | 'italic' | 'strikethrough' | 'monospace') => {
    const el = bodyRef.current;
    const start = el ? el.selectionStart : (selection?.start ?? bodyValue.length);
    const end = el ? el.selectionEnd : (selection?.end ?? bodyValue.length);
    const hasSelection = start !== end;
    const before = bodyValue.substring(0, start);
    const selectedText = hasSelection ? bodyValue.substring(start, end) : 'text';
    const after = bodyValue.substring(end);
    const wrap = format === 'bold' ? '*' : format === 'italic' ? '_' : format === 'strikethrough' ? '~' : '```';
    const newText = before + wrap + selectedText + wrap + after;
    setBodyValue(newText);
    setCurrentTemplate((prev: any) => prev ? { ...prev, templateContent: newText } : null);
    requestAnimationFrame(() => {
      if (el) { el.focus(); const pos = start + wrap.length + selectedText.length + wrap.length; el.setSelectionRange(pos, pos); }
    });
  };

  const insertVariable = () => {
    const existing = (bodyValue.match(/\{\{(\d+)\}\}/g) || []).map(m => parseInt(m.replace(/[{}]/g, '')));
    const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    const el = bodyRef.current;
    const pos = el ? el.selectionStart : bodyValue.length;
    const tag = `{{${next}}}`;
    const newText = bodyValue.substring(0, pos) + tag + bodyValue.substring(pos);
    setBodyValue(newText);
    setCurrentTemplate((prev: any) => prev ? { ...prev, templateContent: newText } : null);
    requestAnimationFrame(() => { if (el) { el.focus(); const newPos = pos + tag.length; el.setSelectionRange(newPos, newPos); } });
  };

  const addButton = () => {
    if (config.buttons.length >= 3) return;
    updateConfig({ buttons: [...config.buttons, { type: 'QUICK_REPLY', text: '' }] });
  };

  const updateButton = (index: number, partial: Partial<WhatsAppBusinessButton>) => {
    const updated = config.buttons.map((btn, i) => i === index ? { ...btn, ...partial } : btn);
    updateConfig({ buttons: updated });
  };

  const removeButton = (index: number) => {
    updateConfig({ buttons: config.buttons.filter((_, i) => i !== index) });
  };

  const handleHeaderMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (headerMediaPreview) URL.revokeObjectURL(headerMediaPreview);
    setHeaderMediaFile(file);
    setHeaderMediaPreview(URL.createObjectURL(file));
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      const mimeType = file.type || 'application/octet-stream';
      let mediaType: 'image' | 'video' | 'document' = 'document';
      if (mimeType.startsWith('image/')) mediaType = 'image';
      else if (mimeType.startsWith('video/')) mediaType = 'video';
      const attachment = [{ type: mediaType, filename: `header-${Date.now()}-${file.name}`, mimeType, size: file.size, data: base64, isNew: true, localUrl: URL.createObjectURL(file) }];
      setCurrentTemplate((prev: any) => prev ? { ...prev, mediaAttachments: attachment } : null);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const getAcceptForHeader = () => {
    if (config.header.type === 'image') return 'image/jpeg,image/png';
    if (config.header.type === 'video') return 'video/mp4';
    if (config.header.type === 'document') return '.pdf';
    return '';
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Row 1: Name, DataSet */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <TextField size="small" label="Template Name" fullWidth
          value={getDisplayTemplateName(currentTemplate?.templateName)}
          onChange={e => {
            const v = e.target.value;
            if (!nameRegex.test(v)) setTemplateNameError('Only lowercase letters, numbers, and underscores are allowed.');
            else setTemplateNameError('');
            setCurrentTemplate((prev: any) => prev ? { ...prev, templateName: v } : null);
          }}
          error={!!templateNameError} helperText={templateNameError || 'Lowercase letters, numbers, and underscores only'}
          placeholder="e.g. order_confirmation"
        />
        <Autocomplete size="small" fullWidth options={dataSets}
          getOptionLabel={(option: any) => getDisplayCollectionName(option.collectionName)}
          value={dataSets.find((ds: any) => ds._id === currentTemplate?.collectionId) || null}
          onChange={(_e, newValue: any) => setCurrentTemplate((prev: any) => prev ? { ...prev, collectionId: newValue?._id || '' } : null)}
          renderInput={(params) => <TextField {...params} label="Select DataSet" InputProps={{ ...params.InputProps, endAdornment: <>{loadingColumns ? <Typography variant="caption">Loading...</Typography> : null}{params.InputProps.endAdornment}</> }} />}
          isOptionEqualToValue={(option: any, value: any) => option._id === value._id}
        />
      </Box>
      {/* Row 2: Language, Category, Access */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mt: 2 }}>
        <TextField select size="small" label="Language" fullWidth value={config.language}
          onChange={e => updateConfig({ language: e.target.value })}>
          {META_LANGUAGES.map(l => <MenuItem key={l.code} value={l.code}>{l.label} ({l.code})</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Category" fullWidth
          value={currentTemplate?.category || ''}
          onChange={e => setCurrentTemplate((prev: any) => prev ? { ...prev, category: e.target.value } : null)}
          SelectProps={{ displayEmpty: true, renderValue: (v) => v ? (META_TEMPLATE_CATEGORIES.find(c => c.value === (v as string))?.label ?? String(v)) : 'Select category' }}
          InputLabelProps={{ shrink: true }}>
          {META_TEMPLATE_CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </TextField>
        <FormControl fullWidth>
          <InputLabel>Access Level</InputLabel>
          <Select size="small" label="Access Level" value={currentTemplate?.accessLevel || ''}
            onChange={e => setCurrentTemplate((prev: any) => prev ? { ...prev, accessLevel: e.target.value } : null)}>
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="private">Private</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {/* Keywords */}
      <Autocomplete multiple freeSolo size="small" options={[]} sx={{ mt: 2 }}
        value={currentTemplate?.keywords || []}
        onChange={(_e, newValue) => setCurrentTemplate((prev: any) => prev ? { ...prev, keywords: newValue as string[] } : null)}
        renderTags={(value, getTagProps) => value.map((option, index) => <Chip size="small" label={option} {...getTagProps({ index })} key={index} />)}
        renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Type and press Enter" />}
      />

      {/* Merge fields */}
      {availableColumns.length > 0 && (
        <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(37,211,102,0.08)' : 'rgba(37,211,102,0.04)', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>Available merge fields (click to insert):</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {availableColumns.map(col => (
              <Chip key={col} label={`{{${col}}}`} size="small" variant="outlined"
                onClick={() => {
                  const el = bodyRef.current; const pos = el ? el.selectionStart : bodyValue.length;
                  const tag = `{{${col}}}`; const newText = bodyValue.substring(0, pos) + tag + bodyValue.substring(pos);
                  setBodyValue(newText); setCurrentTemplate((prev: any) => prev ? { ...prev, templateContent: newText } : null);
                  requestAnimationFrame(() => { if (el) { el.focus(); const newPos = pos + tag.length; el.setSelectionRange(newPos, newPos); } });
                }}
                sx={{ cursor: 'pointer', '&:hover': { borderColor: WA_BIZ_GREEN, color: WA_BIZ_GREEN } }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* HEADER */}
      <WaBizSection title="Header" number={1} optional>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: config.header.type !== 'none' ? 2 : 0 }}>
          {HEADER_TYPES.map(ht => (
            <Button key={ht.value} size="small" variant={config.header.type === ht.value ? 'contained' : 'outlined'} startIcon={ht.icon}
              onClick={() => {
                updateConfig({ header: { type: ht.value, text: ht.value === 'text' ? (config.header.text || '') : undefined } });
                if (ht.value === 'none' || ht.value === 'text') { setHeaderMediaFile(null); if (headerMediaPreview) { URL.revokeObjectURL(headerMediaPreview); setHeaderMediaPreview(''); } setCurrentTemplate((prev: any) => prev ? { ...prev, mediaAttachments: [] } : null); }
              }}
              sx={{ borderColor: config.header.type === ht.value ? WA_BIZ_GREEN : 'divider', backgroundColor: config.header.type === ht.value ? WA_BIZ_GREEN : 'transparent', color: config.header.type === ht.value ? '#fff' : 'text.primary', '&:hover': { backgroundColor: config.header.type === ht.value ? WA_BIZ_GREEN_DARK : 'action.hover' }, textTransform: 'none', minWidth: 80 }}>
              {ht.label}
            </Button>
          ))}
        </Box>
        {config.header.type === 'text' && (
          <TextField size="small" label="Header Text" fullWidth value={config.header.text || ''}
            onChange={e => updateConfig({ header: { type: 'text', text: e.target.value } })}
            helperText={`${(config.header.text || '').length}/60 characters. Use {{1}} for variables.`} inputProps={{ maxLength: 60 }} />
        )}
        {(config.header.type === 'image' || config.header.type === 'video' || config.header.type === 'document') && (
          <Box>
            <input type="file" accept={getAcceptForHeader()} onChange={handleHeaderMediaChange} style={{ display: 'none' }} id="wa-biz-user-header-media" />
            <label htmlFor="wa-biz-user-header-media">
              <Button variant="outlined" component="span" startIcon={<AttachFile />}
                sx={{ borderColor: WA_BIZ_GREEN, color: WA_BIZ_GREEN, '&:hover': { borderColor: WA_BIZ_GREEN_DARK, backgroundColor: 'rgba(37,211,102,0.08)' } }}>
                Upload {config.header.type === 'image' ? 'Image (JPEG/PNG)' : config.header.type === 'video' ? 'Video (MP4)' : 'Document (PDF)'}
              </Button>
            </label>
            {headerMediaPreview && config.header.type === 'image' && (
              <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                <img src={headerMediaPreview} alt="Header" style={{ maxHeight: 120, borderRadius: 8, border: '1px solid #ddd' }} />
                <IconButton size="small" onClick={() => { setHeaderMediaFile(null); URL.revokeObjectURL(headerMediaPreview); setHeaderMediaPreview(''); setCurrentTemplate((prev: any) => prev ? { ...prev, mediaAttachments: [] } : null); }}
                  sx={{ position: 'absolute', top: -8, right: -8, backgroundColor: 'error.main', color: '#fff', '&:hover': { backgroundColor: 'error.dark' }, width: 24, height: 24 }}>
                  <Delete sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )}
            {headerMediaFile && config.header.type !== 'image' && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {config.header.type === 'video' ? <Videocam sx={{ color: WA_BIZ_GREEN }} /> : <InsertDriveFile sx={{ color: WA_BIZ_GREEN }} />}
                <Typography variant="body2" sx={{ flex: 1 }}>{headerMediaFile.name}</Typography>
                <IconButton size="small" onClick={() => { setHeaderMediaFile(null); if (headerMediaPreview) { URL.revokeObjectURL(headerMediaPreview); setHeaderMediaPreview(''); } setCurrentTemplate((prev: any) => prev ? { ...prev, mediaAttachments: [] } : null); }} sx={{ color: 'error.main' }}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        )}
      </WaBizSection>

      {/* BODY */}
      <WaBizSection title="Body" number={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {bodyValue.length}/1024 characters
            {bodyValue.length > 1024 && <span style={{ color: '#d32f2f', fontWeight: 700 }}> — Exceeds limit!</span>}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button size="small" variant="outlined" onClick={insertVariable}
              sx={{ textTransform: 'none', fontSize: 12, borderColor: WA_BIZ_GREEN, color: WA_BIZ_GREEN, '&:hover': { borderColor: WA_BIZ_GREEN_DARK, backgroundColor: 'rgba(37,211,102,0.08)' } }}>
              + Variable
            </Button>
            <Button size="small" variant={showPreview ? "contained" : "outlined"} startIcon={showPreview ? <Edit /> : <Visibility />}
              onClick={() => setShowPreview(!showPreview)}
              sx={{ textTransform: 'none', fontSize: 12, backgroundColor: showPreview ? WA_BIZ_GREEN : 'transparent', color: showPreview ? '#fff' : WA_BIZ_GREEN, borderColor: WA_BIZ_GREEN, '&:hover': { backgroundColor: showPreview ? WA_BIZ_GREEN_DARK : 'rgba(37,211,102,0.08)' } }}>
              {showPreview ? 'Edit' : 'Preview'}
            </Button>
          </Box>
        </Box>
        {showPreview ? (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {config.header.type !== 'none' && (
              <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(37,211,102,0.1)' : 'rgba(37,211,102,0.05)' }}>
                {config.header.type === 'text' && <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{config.header.text || 'Header text'}</Typography>}
                {config.header.type === 'image' && (headerMediaPreview ? <img src={headerMediaPreview} alt="Header" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }} /> : <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'action.hover', borderRadius: 1 }}><ImageOutlined sx={{ fontSize: 40, color: 'text.secondary' }} /></Box>)}
                {config.header.type === 'video' && <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'action.hover', borderRadius: 1 }}><Videocam sx={{ fontSize: 40, color: 'text.secondary' }} /></Box>}
                {config.header.type === 'document' && <Box sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, backgroundColor: 'action.hover', borderRadius: 1 }}><InsertDriveFile sx={{ fontSize: 32, color: 'text.secondary' }} /><Typography color="text.secondary">Document</Typography></Box>}
              </Box>
            )}
            <Box sx={{ p: 2 }}>
              <Box sx={{ background: theme => theme.palette.mode === 'dark' ? '#1e3a29' : '#dcf8c6', borderRadius: '0 12px 12px 12px', p: 1.5, maxWidth: '90%', '& strong': { fontWeight: 700 }, '& em': { fontStyle: 'italic' }, '& del': { textDecoration: 'line-through' } }}
                dangerouslySetInnerHTML={{ __html: formatWhatsAppBiz(bodyValue) || '<span style="color:#999">Body text goes here...</span>' }} />
            </Box>
            {config.footer && <Box sx={{ px: 2, pb: 1 }}><Typography variant="caption" color="text.secondary">{config.footer}</Typography></Box>}
            {config.buttons.length > 0 && (
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                {config.buttons.map((btn, i) => (
                  <Box key={i} sx={{ textAlign: 'center', py: 1, borderBottom: i < config.buttons.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ color: '#0088cc', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      {btn.type === 'URL' && <OpenInNew sx={{ fontSize: 14 }} />}{btn.type === 'PHONE_NUMBER' && <Phone sx={{ fontSize: 14 }} />}{btn.type === 'QUICK_REPLY' && <Reply sx={{ fontSize: 14 }} />}{btn.text || 'Button text'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ background: theme => theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f0f2f5', borderBottom: '1px solid', borderColor: 'divider', padding: '6px 12px', display: 'flex', gap: '2px', alignItems: 'center' }}>
              <ToggleButtonGroup size="small" sx={{ '& .MuiToggleButton-root': { border: 'none', color: theme => theme.palette.mode === 'dark' ? theme.palette.grey[400] : '#54656f', px: 1 } }}>
                <ToggleButton value="bold" onClick={() => applyFormatting('bold')} sx={{ '&:hover': { color: WA_BIZ_GREEN } }}><FormatBold fontSize="small" /></ToggleButton>
                <ToggleButton value="italic" onClick={() => applyFormatting('italic')} sx={{ '&:hover': { color: WA_BIZ_GREEN } }}><FormatItalic fontSize="small" /></ToggleButton>
                <ToggleButton value="strikethrough" onClick={() => applyFormatting('strikethrough')} sx={{ '&:hover': { color: WA_BIZ_GREEN } }}><FormatStrikethrough fontSize="small" /></ToggleButton>
                <ToggleButton value="monospace" onClick={() => applyFormatting('monospace')} sx={{ '&:hover': { color: WA_BIZ_GREEN } }}><Code fontSize="small" /></ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <TextField multiline minRows={6} maxRows={12} value={bodyValue}
              onChange={e => { setBodyValue(e.target.value); setCurrentTemplate((prev: any) => prev ? { ...prev, templateContent: e.target.value } : null); }}
              onSelect={e => { const t = e.target as HTMLTextAreaElement; setSelection({ start: t.selectionStart, end: t.selectionEnd }); }}
              inputRef={bodyRef} variant="standard" fullWidth
              InputProps={{ disableUnderline: true, style: { padding: '12px 16px', fontSize: '14px', lineHeight: '1.5' } }}
              sx={{ '& .MuiInputBase-root': { minHeight: '150px', color: theme => theme.palette.text.primary, backgroundColor: theme => theme.palette.background.paper }, width: '100%' }}
              placeholder="Enter the body text of your message. Use {{1}}, {{2}}, etc. for variables."
              error={bodyValue.length > 1024}
            />
          </Box>
        )}
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(37,211,102,0.12)' : 'rgba(37,211,102,0.06)', border: '1px solid', borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(37,211,102,0.25)' : 'rgba(37,211,102,0.15)' }}>
          <Typography variant="caption" color="text.secondary" component="div">
            <strong>Formatting:</strong> *bold* &nbsp; _italic_ &nbsp; ~strikethrough~ &nbsp; {'```monospace```'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            <strong>Variables:</strong> Use {'{{1}}'}, {'{{2}}'}, {'{{3}}'} etc. Variables must be sequential starting from 1, with no gaps. Do not place variables at the start/end without surrounding text, and do not place two variables adjacent to each other.
          </Typography>
        </Box>
      </WaBizSection>

      {/* FOOTER */}
      <WaBizSection title="Footer" number={3} optional>
        <TextField size="small" label="Footer Text" fullWidth value={config.footer}
          onChange={e => updateConfig({ footer: e.target.value })}
          helperText={`${(config.footer || '').length}/60 characters`} inputProps={{ maxLength: 60 }}
          placeholder="e.g. Reply STOP to unsubscribe" />
      </WaBizSection>

      {/* BUTTONS */}
      <WaBizSection title="Buttons" number={4} optional>
        {config.buttons.map((btn, idx) => (
          <Box key={idx} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>Button {idx + 1}</Typography>
              <IconButton size="small" onClick={() => removeButton(idx)} sx={{ color: 'error.main' }}><Remove fontSize="small" /></IconButton>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, gap: 1.5 }}>
              <TextField select size="small" label="Type" fullWidth value={btn.type}
                onChange={e => updateButton(idx, { type: e.target.value as WhatsAppBusinessButton['type'], url: undefined, phoneNumber: undefined })}>
                {BUTTON_TYPES.map(bt => <MenuItem key={bt.value} value={bt.value}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{bt.icon}{bt.label}</Box></MenuItem>)}
              </TextField>
              <TextField size="small" label="Button Text" fullWidth value={btn.text}
                onChange={e => updateButton(idx, { text: e.target.value })} inputProps={{ maxLength: 25 }} helperText={`${(btn.text || '').length}/25`} />
            </Box>
            {btn.type === 'URL' && <TextField size="small" label="URL" fullWidth value={btn.url || ''} onChange={e => updateButton(idx, { url: e.target.value })} placeholder="https://www.example.com/{{1}}" sx={{ mt: 1.5 }} helperText="Use {{1}} for a dynamic URL suffix." />}
            {btn.type === 'PHONE_NUMBER' && <TextField size="small" label="Phone Number" fullWidth value={btn.phoneNumber || ''} onChange={e => updateButton(idx, { phoneNumber: e.target.value })} placeholder="+1234567890" sx={{ mt: 1.5 }} helperText="Include country code." />}
          </Box>
        ))}
        {config.buttons.length < 3 && (
          <Button variant="outlined" startIcon={<Add />} onClick={addButton} fullWidth
            sx={{ borderStyle: 'dashed', textTransform: 'none', borderColor: WA_BIZ_GREEN, color: WA_BIZ_GREEN, '&:hover': { borderColor: WA_BIZ_GREEN_DARK, backgroundColor: 'rgba(37,211,102,0.06)' } }}>
            Add Button ({config.buttons.length}/3)
          </Button>
        )}
      </WaBizSection>

      {/* Meta status */}
      {currentTemplate?.metaStatus && (
        <Paper variant="outlined" sx={{ p: 1.5, mt: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1,
          borderColor: currentTemplate.metaStatus === 'APPROVED' ? 'success.main' : currentTemplate.metaStatus === 'REJECTED' ? 'error.main' : 'warning.main' }}>
          <Chip size="small" label={currentTemplate.metaStatus}
            color={currentTemplate.metaStatus === 'APPROVED' ? 'success' : currentTemplate.metaStatus === 'REJECTED' ? 'error' : 'warning'}
            sx={{ fontWeight: 700 }} />
          {currentTemplate.metaStatusMessage && <Typography variant="caption" color="text.secondary">{currentTemplate.metaStatusMessage}</Typography>}
        </Paper>
      )}
    </Box>
  );
};

export default WhatsAppBusinessTemplate;
